import { useEffect, useMemo } from 'react'
import { useFieldArray, useForm } from 'react-hook-form'
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { Textarea } from '@/components/ui/textarea'
import { Select } from '@/components/ui/Select'
import { Button } from '@/components/ui/button'
import { Label } from '@/components/ui/label'
import { toast } from 'react-toastify'
import {
  CreateQuizQuestionPayload,
  QuizQuestion,
  QuizQuestionType,
  UpdateQuizQuestionPayload,
} from '@/types/quizzes'

type OptionField = {
  key: string
  text: string
}

type RubricField = {
  criteria: string
  points: number
}

export interface QuizQuestionFormValues {
  type: QuizQuestionType
  questionText: string
  scoreWeight: number
  options: OptionField[]
  correctOption?: string
  correctOptions?: string[]
  numericValue?: number | null
  numericTolerance?: number | null
  shortAnswerExpected?: string | null
  shortAnswerHint?: string | null
  shortAnswerRubric: RubricField[]
  fileMaxFiles?: number | null
  fileMaxSize?: number | null
  fileMimeTypes?: string
  fileInstructions?: string | null
}

export interface QuizQuestionFormProps {
  mode?: 'create' | 'update'
  question?: QuizQuestion | null
  isSubmitting?: boolean
  onSubmit: (
    payload: CreateQuizQuestionPayload | UpdateQuizQuestionPayload,
  ) => Promise<void> | void
  onCancel?: () => void
}

const QUESTION_TYPE_OPTIONS: { label: string; value: QuizQuestionType; description: string }[] = [
  {
    value: 'mcq',
    label: 'Single answer (multiple choice)',
    description: 'Learners pick one correct answer from several options.',
  },
  {
    value: 'multi',
    label: 'Multiple answers',
    description: 'Learners can choose more than one correct answer.',
  },
  {
    value: 'short_answer',
    label: 'Short written answer',
    description: 'Learners type a short response that can be graded manually.',
  },
  {
    value: 'numeric',
    label: 'Numeric answer',
    description: 'Learners provide a precise number, optionally with tolerance.',
  },
  {
    value: 'file_upload',
    label: 'File upload',
    description: 'Learners submit one or more files for review.',
  },
]

const DEFAULT_OPTIONS: OptionField[] = [
  { key: 'A', text: '' },
  { key: 'B', text: '' },
]

const createDefaultFormValues = (type: QuizQuestionType = 'mcq'): QuizQuestionFormValues => ({
  type,
  questionText: '',
  scoreWeight: 1,
  options: DEFAULT_OPTIONS.map((option, index) => ({
    key: option.key ?? String.fromCharCode(65 + index),
    text: option.text ?? '',
  })),
  correctOption: 'A',
  correctOptions: [],
  numericValue: null,
  numericTolerance: 0,
  shortAnswerExpected: '',
  shortAnswerHint: '',
  shortAnswerRubric: [],
  fileMaxFiles: 1,
  fileMaxSize: 5,
  fileMimeTypes: 'application/pdf,image/jpeg,image/png',
  fileInstructions: '',
})

const mapQuestionToFormValues = (question?: QuizQuestion | null): QuizQuestionFormValues => {
  if (!question) return createDefaultFormValues()
  if (question.type === 'mcq') {
    return {
      ...createDefaultFormValues('mcq'),
      type: 'mcq',
      questionText: question.questionText,
      scoreWeight: question.scoreWeight ?? 1,
      options: (question.options ?? DEFAULT_OPTIONS).map((option, index) => ({
        key: option.key ?? String.fromCharCode(65 + index),
        text: option.text ?? '',
      })),
      correctOption: (question.correctAnswer as string | null) ?? undefined,
    }
  }
  if (question.type === 'multi') {
    return {
      ...createDefaultFormValues('multi'),
      type: 'multi',
      questionText: question.questionText,
      scoreWeight: question.scoreWeight ?? 1,
      options: (question.options ?? DEFAULT_OPTIONS).map((option, index) => ({
        key: option.key ?? String.fromCharCode(65 + index),
        text: option.text ?? '',
      })),
      correctOptions: Array.isArray(question.correctAnswer) ? question.correctAnswer : [],
    }
  }
  if (question.type === 'numeric') {
    const answer = question.correctAnswer
    return {
      ...createDefaultFormValues('numeric'),
      type: 'numeric',
      questionText: question.questionText,
      scoreWeight: question.scoreWeight ?? 1,
      numericValue:
        typeof answer === 'object' && answer
          ? Number((answer as { value?: number }).value ?? 0)
          : Number(answer ?? 0),
      numericTolerance:
        typeof answer === 'object' && answer
          ? Number((answer as { tolerance?: number }).tolerance ?? 0)
          : 0,
    }
  }
  if (question.type === 'short_answer') {
    const metadata = question.metadata ?? {}
    const rubric = Array.isArray((metadata as any).rubric)
      ? (metadata as any).rubric.map((item: any) => ({
          criteria: item.criteria ?? '',
          points: Number(item.points ?? 0),
        }))
      : []
    return {
      ...createDefaultFormValues('short_answer'),
      type: 'short_answer',
      questionText: question.questionText,
      scoreWeight: question.scoreWeight ?? 1,
      shortAnswerExpected: (question.correctAnswer as string | null) ?? '',
      shortAnswerHint: (metadata as any).hint ?? '',
      shortAnswerRubric: rubric,
    }
  }
  if (question.type === 'file_upload') {
    const metadata = question.metadata ?? {}
    const allowedMimeTypes = Array.isArray((metadata as any).allowedMimeTypes)
      ? (metadata as any).allowedMimeTypes.join(',')
      : ''
    return {
      ...createDefaultFormValues('file_upload'),
      type: 'file_upload',
      questionText: question.questionText,
      scoreWeight: question.scoreWeight ?? 0,
      fileMaxFiles: Number((metadata as any).maxFiles ?? 1),
      fileMaxSize:
        Number((metadata as any).maxFileSizeBytes ?? 5000000) / 1000000 || 5,
      fileMimeTypes: allowedMimeTypes ?? '',
      fileInstructions: (metadata as any).instructions ?? '',
    }
  }
  return createDefaultFormValues()
}

export function QuizQuestionForm({
  mode = 'create',
  question,
  isSubmitting,
  onSubmit,
  onCancel,
}: QuizQuestionFormProps) {
  const defaultValues = useMemo(() => mapQuestionToFormValues(question), [question])

  const {
    register,
    control,
    handleSubmit,
    watch,
    reset,
    setError,
    clearErrors,
    formState: { errors },
  } = useForm<QuizQuestionFormValues>({
    defaultValues,
  })

  const { fields: optionFields, append: appendOption, remove: removeOption } = useFieldArray({
    control,
    name: 'options',
  })

  const {
    fields: rubricFields,
    append: appendRubric,
    remove: removeRubric,
  } = useFieldArray({
    control,
    name: 'shortAnswerRubric',
  })

  const currentType = watch('type')

  useEffect(() => {
    reset(mapQuestionToFormValues(question))
  }, [question, reset])

  useEffect(() => {
    if (currentType === 'mcq' || currentType === 'multi') {
      if (optionFields.length === 0) {
        appendOption({ key: 'A', text: '' })
        appendOption({ key: 'B', text: '' })
      }
    }
  }, [appendOption, currentType, optionFields.length])

  const ensureOptionKeys = (options: OptionField[]) => {
    options.forEach((option, index) => {
      if (!option.key) {
        option.key = String.fromCharCode(65 + index)
      }
    })
    return options
  }

  const buildPayload = (values: QuizQuestionFormValues): CreateQuizQuestionPayload | UpdateQuizQuestionPayload | null => {
    const base: Partial<CreateQuizQuestionPayload> = {
      questionText: values.questionText.trim(),
      scoreWeight: Number.isNaN(values.scoreWeight) ? 0 : Number(values.scoreWeight),
    }

    if (values.type === 'mcq') {
      const options = ensureOptionKeys(values.options)
      if (options.some((option) => !option.text.trim())) {
        setError('options', { message: 'Please fill in all answer choices.' })
        return null
      }
      if (!values.correctOption) {
        setError('correctOption', { message: 'Select the correct answer.' })
        return null
      }
      clearErrors(['options', 'correctOption'])
      return {
        ...(base as CreateQuizQuestionPayload),
        type: 'mcq',
        options,
        correctAnswer: values.correctOption,
      }
    }

    if (values.type === 'multi') {
      const options = ensureOptionKeys(values.options)
      if (options.some((option) => !option.text.trim())) {
        setError('options', { message: 'Please fill in all answer choices.' })
        return null
      }
      if (!values.correctOptions || values.correctOptions.length === 0) {
        setError('correctOptions', { message: 'Select at least one correct answer.' })
        return null
      }
      clearErrors(['options', 'correctOptions'])
      return {
        ...(base as CreateQuizQuestionPayload),
        type: 'multi',
        options,
        correctAnswer: Array.from(new Set(values.correctOptions)),
      }
    }

    if (values.type === 'numeric') {
      if (values.numericValue === null || Number.isNaN(values.numericValue)) {
        setError('numericValue', { message: 'Provide the expected numeric value.' })
        return null
      }
      clearErrors('numericValue')
      return {
        ...(base as CreateQuizQuestionPayload),
        type: 'numeric',
        correctAnswer: {
          value: Number(values.numericValue),
          tolerance: values.numericTolerance ?? undefined,
        },
      }
    }

    if (values.type === 'short_answer') {
      return {
        ...(base as CreateQuizQuestionPayload),
        type: 'short_answer',
        correctAnswer: values.shortAnswerExpected?.trim() || undefined,
        metadata: {
          grading: 'manual',
          hint: values.shortAnswerHint?.trim() || undefined,
          rubric: values.shortAnswerRubric
            .filter((item) => item.criteria.trim())
            .map((item) => ({
              criteria: item.criteria.trim(),
              points: Number(item.points ?? 0),
            })),
        },
      }
    }

    if (values.type === 'file_upload') {
      const mimeTypes = values.fileMimeTypes
        ?.split(',')
        .map((item) => item.trim())
        .filter(Boolean)
      return {
        ...(base as CreateQuizQuestionPayload),
        type: 'file_upload',
        metadata: {
          maxFiles: values.fileMaxFiles ?? undefined,
          maxFileSizeBytes: values.fileMaxSize ? values.fileMaxSize * 1000000 : undefined,
          allowedMimeTypes: mimeTypes?.length ? mimeTypes : undefined,
          instructions: values.fileInstructions?.trim() || undefined,
          scoring: {
            method: 'manual',
            notes: 'Review uploaded files and assign a fair score.',
          },
        },
      }
    }

    return null
  }

  const handleFormSubmit = async (values: QuizQuestionFormValues) => {
    const payload = buildPayload(values)
    if (!payload) {
      toast.error('Please review the form and fix any highlighted fields.')
      return
    }

    if (mode === 'update' && question) {
      await onSubmit({
        ...(payload as CreateQuizQuestionPayload),
        id: question.id,
        type: question.type,
      } as UpdateQuizQuestionPayload)
    } else {
      await onSubmit(payload)
    }
  }

  const showOptions = currentType === 'mcq' || currentType === 'multi'
  const showMultiSelect = currentType === 'multi'
  const showNumeric = currentType === 'numeric'
  const showShortAnswer = currentType === 'short_answer'
  const showFileUpload = currentType === 'file_upload'

  return (
    <form onSubmit={handleSubmit(handleFormSubmit)} className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle>{mode === 'create' ? 'Add a new question' : 'Update question'}</CardTitle>
          <CardDescription>
            Be clear and concise so learners understand what you expect in their answers.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid gap-4 md:grid-cols-2">
            <div className="space-y-2">
              <Label htmlFor="type">Question format</Label>
              <Select
                id="type"
                disabled={mode === 'update'}
                value={watch('type')}
                onChange={(event) => {
                  if (mode === 'update') return
                  const nextType = event.target.value as QuizQuestionType
                  reset(createDefaultFormValues(nextType))
                }}
              >
                {QUESTION_TYPE_OPTIONS.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </Select>
              <p className="text-xs text-gray-500">
                {QUESTION_TYPE_OPTIONS.find((option) => option.value === watch('type'))?.description}
              </p>
            </div>
            <div className="space-y-2">
              <Label htmlFor="scoreWeight">Score weight</Label>
              <Input
                id="scoreWeight"
                type="number"
                step="0.5"
                min={0}
                {...register('scoreWeight', { valueAsNumber: true })}
              />
              {errors.scoreWeight && (
                <p className="text-sm text-red-600">{errors.scoreWeight.message}</p>
              )}
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="questionText">Question prompt</Label>
            <Textarea
              id="questionText"
              rows={4}
              placeholder="What would you like learners to answer?"
              {...register('questionText', { required: true })}
            />
            {errors.questionText && (
              <p className="text-sm text-red-600">Please write a clear question prompt.</p>
            )}
          </div>

        </CardContent>
      </Card>

      {showOptions && (
        <Card>
          <CardHeader>
            <CardTitle>Answer options</CardTitle>
            <CardDescription>
              Provide at least two options. Make sure only one option is marked correct for single
              answer questions.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            {optionFields.map((field, index) => (
              <div key={field.id} className="grid gap-3 rounded-lg border border-gray-200 p-4 md:grid-cols-[100px_1fr_auto]">
                <div className="space-y-2">
                  <Label htmlFor={`options.${index}.key`}>Label</Label>
                  <Input
                    id={`options.${index}.key`}
                    placeholder="A"
                    {...register(`options.${index}.key` as const)}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor={`options.${index}.text`}>Answer text</Label>
                  <Input
                    id={`options.${index}.text`}
                    placeholder="Type the answer choice"
                    {...register(`options.${index}.text` as const)}
                  />
                </div>
                <div className="flex items-end justify-end gap-2">
                  <label className="flex items-center gap-2 text-sm">
                    <input
                      type={showMultiSelect ? 'checkbox' : 'radio'}
                      value={field.key ?? ''}
                      {...register(showMultiSelect ? 'correctOptions' : 'correctOption')}
                      checked={
                        showMultiSelect
                          ? watch('correctOptions')?.includes(field.key ?? '') ?? false
                          : watch('correctOption') === field.key
                      }
                    />
                    {showMultiSelect ? 'Correct' : 'Correct answer'}
                  </label>
                  {optionFields.length > 2 && (
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      onClick={() => removeOption(index)}
                    >
                      Remove
                    </Button>
                  )}
                </div>
              </div>
            ))}
            <Button
              type="button"
              variant="outline"
              onClick={() =>
                appendOption({
                  key: String.fromCharCode(65 + optionFields.length),
                  text: '',
                })
              }
            >
              Add another option
            </Button>
            {errors.options && (
              <p className="text-sm text-red-600">{errors.options.message as string}</p>
            )}
            {errors.correctOption && (
              <p className="text-sm text-red-600">{errors.correctOption.message}</p>
            )}
            {errors.correctOptions && (
              <p className="text-sm text-red-600">{errors.correctOptions.message}</p>
            )}
          </CardContent>
        </Card>
      )}

      {showNumeric && (
        <Card>
          <CardHeader>
            <CardTitle>Expected value</CardTitle>
            <CardDescription>
              Provide the correct number. If you want to accept a range, set an acceptable
              difference (tolerance).
            </CardDescription>
          </CardHeader>
          <CardContent className="grid gap-4 md:grid-cols-2">
            <div className="space-y-2">
              <Label htmlFor="numericValue">Correct value</Label>
              <Input
                id="numericValue"
                type="number"
                step="0.01"
                {...register('numericValue', { valueAsNumber: true })}
              />
              {errors.numericValue && (
                <p className="text-sm text-red-600">{errors.numericValue.message}</p>
              )}
            </div>
            <div className="space-y-2">
              <Label htmlFor="numericTolerance">Acceptable tolerance</Label>
              <Input
                id="numericTolerance"
                type="number"
                step="0.01"
                placeholder="Optional"
                {...register('numericTolerance', { valueAsNumber: true })}
              />
            </div>
          </CardContent>
        </Card>
      )}

      {showShortAnswer && (
        <Card>
          <CardHeader>
            <CardTitle>Guidance for reviewers</CardTitle>
            <CardDescription>
              Add helpful notes so instructors can grade consistently.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="shortAnswerExpected">Sample correct answer (optional)</Label>
              <Textarea
                id="shortAnswerExpected"
                rows={3}
                placeholder="Provide a model answer or key points to cover."
                {...register('shortAnswerExpected')}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="shortAnswerHint">Hint for learners (optional)</Label>
              <Textarea
                id="shortAnswerHint"
                rows={2}
                placeholder="Offer a gentle reminder or clue if helpful."
                {...register('shortAnswerHint')}
              />
            </div>
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <div>
                  <h4 className="text-sm font-semibold text-gray-800">Scoring rubric (optional)</h4>
                  <p className="text-xs text-gray-500">
                    Break the answer into criteria with points for each part.
                  </p>
                </div>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => appendRubric({ criteria: '', points: 0 })}
                >
                  Add criteria
                </Button>
              </div>
              {rubricFields.length === 0 && (
                <p className="text-sm text-gray-500">
                  No rubric items yet. Add criteria to help with consistent grading.
                </p>
              )}
              {rubricFields.map((field, index) => (
                <div
                  key={field.id}
                  className="grid gap-3 rounded border border-gray-200 p-3 md:grid-cols-[2fr_1fr_auto]"
                >
                  <div className="space-y-1.5">
                    <Label htmlFor={`shortAnswerRubric.${index}.criteria`}>Criteria</Label>
                    <Input
                      id={`shortAnswerRubric.${index}.criteria`}
                      {...register(`shortAnswerRubric.${index}.criteria` as const)}
                    />
                  </div>
                  <div className="space-y-1.5">
                    <Label htmlFor={`shortAnswerRubric.${index}.points`}>Points</Label>
                    <Input
                      id={`shortAnswerRubric.${index}.points`}
                      type="number"
                      step="0.5"
                      {...register(`shortAnswerRubric.${index}.points` as const, {
                        valueAsNumber: true,
                      })}
                    />
                  </div>
                  <div className="flex items-end justify-end">
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      onClick={() => removeRubric(index)}
                    >
                      Remove
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {showFileUpload && (
        <Card>
          <CardHeader>
            <CardTitle>File upload settings</CardTitle>
            <CardDescription>
              Set friendly limits so learners know exactly what is expected.
            </CardDescription>
          </CardHeader>
          <CardContent className="grid gap-4 md:grid-cols-2">
            <div className="space-y-2">
              <Label htmlFor="fileMaxFiles">Maximum files</Label>
              <Input
                id="fileMaxFiles"
                type="number"
                min={1}
                {...register('fileMaxFiles', { valueAsNumber: true })}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="fileMaxSize">Maximum size per file (MB)</Label>
              <Input
                id="fileMaxSize"
                type="number"
                min={1}
                step="1"
                {...register('fileMaxSize', { valueAsNumber: true })}
              />
            </div>
            <div className="space-y-2 md:col-span-2">
              <Label htmlFor="fileMimeTypes">Accepted file types</Label>
              <Input
                id="fileMimeTypes"
                placeholder="Comma-separated list, e.g. application/pdf,image/jpeg"
                {...register('fileMimeTypes')}
              />
              <p className="text-xs text-gray-500">
                Leave blank to allow any file type. Otherwise provide a comma-separated list.
              </p>
            </div>
            <div className="space-y-2 md:col-span-2">
              <Label htmlFor="fileInstructions">Additional instructions</Label>
              <Textarea
                id="fileInstructions"
                rows={3}
                placeholder="Share any instructions learners should follow when uploading files."
                {...register('fileInstructions')}
              />
            </div>
          </CardContent>
        </Card>
      )}

      <div className="flex items-center justify-between gap-3">
        {onCancel && (
          <Button type="button" variant="ghost" onClick={onCancel}>
            Cancel
          </Button>
        )}
        <Button type="submit" disabled={isSubmitting}>
          {isSubmitting ? 'Saving...' : mode === 'create' ? 'Add question' : 'Update question'}
        </Button>
      </div>
    </form>
  )
}
